import { useEffect, useRef, useCallback } from 'react';

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface UseFocusTrapOptions {
  /** Whether the focus trap is active */
  isActive: boolean;
  /** Callback when Escape key is pressed */
  onEscape?: () => void;
  /** Whether to close on Escape key (default: true) */
  closeOnEscape?: boolean;
  /** Whether to auto-focus first element (default: true) */
  autoFocus?: boolean;
}

/**
 * Hook that traps focus within a container element
 * Implements WCAG 2.1 focus management for modal dialogs
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>({
  isActive,
  onEscape,
  closeOnEscape = true,
  autoFocus = true,
}: UseFocusTrapOptions) {
  const containerRef = useRef<T>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const hasAutoFocused = useRef(false);

  // Use refs for callbacks to avoid re-running effects when they change
  const onEscapeRef = useRef(onEscape);
  const closeOnEscapeRef = useRef(closeOnEscape);

  // Keep refs updated with latest values
  useEffect(() => {
    onEscapeRef.current = onEscape;
    closeOnEscapeRef.current = closeOnEscape;
  }, [onEscape, closeOnEscape]);

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
    ).filter(el => el.offsetParent !== null); // Filter out hidden elements
  }, []);

  // Setup and cleanup - only depends on isActive and autoFocus
  useEffect(() => {
    if (!isActive) return;

    // Save the currently focused element (only when modal opens)
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Handle Tab key to trap focus
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!containerRef.current) return;

      // Handle Escape key
      if (event.key === 'Escape' && closeOnEscapeRef.current) {
        event.preventDefault();
        onEscapeRef.current?.();
        return;
      }

      // Handle Tab key for focus trapping
      if (event.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        // Shift + Tab: Move focus backwards
        if (event.shiftKey) {
          if (activeElement === firstElement || !containerRef.current.contains(activeElement)) {
            event.preventDefault();
            lastElement.focus();
          }
        }
        // Tab: Move focus forwards
        else {
          if (activeElement === lastElement || !containerRef.current.contains(activeElement)) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Auto-focus first focusable element
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (autoFocus && !hasAutoFocused.current) {
      hasAutoFocused.current = true;
      // Use setTimeout to ensure the modal is rendered
      timeoutId = setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else {
          // If no focusable elements, focus the container itself
          containerRef.current?.focus();
        }
      }, 10);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the previously focused element (only when modal closes)
      previousActiveElement.current?.focus();
    };
  }, [isActive, autoFocus, getFocusableElements]);

  useEffect(() => {
    if (!isActive) {
      hasAutoFocused.current = false;
    }
  }, [isActive]);

  return { containerRef };
}
